import { Skeleton } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Markdown } from "@/components/chat/Markdown";
import { Text } from "@/components/Text";
import { DocumentPreview } from "@/features/chat/documents/DocumentPreview";
import type { PreviewFile } from "@/features/chat/documents/previewFile";
import { TextDocumentPreview } from "@/features/chat/documents/TextDocumentPreview";
import type { FileView, KnowledgeFile } from "./filePreview";

export type FilePreviewTab = "preview" | "source" | "extracted";

function PreviewMessage({ message }: Readonly<{ message: string }>) {
  return (
    <output className="block rounded-lg border border-border-subtle bg-surface-secondary px-4 py-6 text-center">
      <Text tone="secondary">{message}</Text>
    </output>
  );
}

function useObjectUrl(blob: Blob | undefined) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) return;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

function ImagePreview({ file, title }: Readonly<{ file: PreviewFile; title: string }>) {
  const { t } = useTranslation("common", { keyPrefix: "filePreview" });
  const image = useQuery({
    queryKey: [...file.key, "original"],
    queryFn: file.original,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const url = useObjectUrl(image.data?.blob);
  if (image.isError) return <PreviewMessage message={t("failed")} />;
  if (!url) return <Skeleton className="h-64 w-full rounded-md" />;
  return <img src={url} alt={title} className="w-full rounded-md border border-border-subtle" />;
}

/** The AI-ready text the indexer extracted from the file: what agents actually search. */
function ExtractedText({ file }: Readonly<{ file: KnowledgeFile }>) {
  const { t } = useTranslation("common", { keyPrefix: "filePreview" });
  const markdown = useQuery({
    queryKey: [...file.key, "markdown"],
    queryFn: file.markdown,
    retry: false,
  });
  if (markdown.isPending) return <Skeleton className="h-64 w-full rounded-md" />;
  if (markdown.isError) return <PreviewMessage message={t("failed")} />;
  return (
    <div className="space-y-3">
      <Text variant="caption" tone="tertiary">
        {t("extractedHint")}
      </Text>
      <Markdown>{markdown.data}</Markdown>
    </div>
  );
}

export function FilePreviewContent({
  file,
  view,
  tab,
  title,
}: Readonly<{ file: KnowledgeFile; view: FileView; tab: FilePreviewTab; title: string }>) {
  if (tab === "extracted" || view.kind === "extracted") return <ExtractedText file={file} />;
  if (view.kind === "text") {
    return (
      <TextDocumentPreview file={file} kind={view.text} title={title} view={tab === "source" ? "source" : "preview"} />
    );
  }
  if (view.kind === "image") return <ImagePreview file={file} title={title} />;
  return <DocumentPreview file={file} />;
}
